import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Dialog,
  DialogContent,
  useScrollTrigger,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import mpactLogo from '../images/MPact_logo.png';
import propvizerLogo from '../images/propvizer.png';
import sentientLogo from '../images/Sentient Co Logo.jpeg';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import LoginForm from './LoginForm';
import { ADMIN_EMAILS } from '../constants';

// Elevation on scroll
function ElevationScroll(props: { children: React.ReactElement<any, any> }) {
  const { children } = props;
  const location = useLocation();
  const isAboutPage = location.pathname === '/about' || location.pathname === '/';
  
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    sx: {
      // When scrolled down, use dark background; when at top, be transparent only on about page
      backgroundColor: trigger 
        ? 'primary.dark'  // Dark blue-purple when scrolled
        : isAboutPage 
          ? 'transparent' 
          : 'primary.dark', // Dark blue-purple on other pages
      color: 'white', // Always white text
      transition: 'all 0.3s ease-in-out',
      boxShadow: trigger ? '0 2px 10px rgba(0,0,0,0.2)' : 'none',
    },
    elevation: trigger ? 4 : 0,
  });
}

const Header: React.FC = () => {
  const { isAuthenticated, currentUser, logout } = useAuth();
  const { tenantConfig, isLoading: tenantLoading } = useTenant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState<null | HTMLElement>(null);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [isTransparent, setIsTransparent] = useState(true);
  const open = Boolean(anchorEl);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const location = useLocation();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isXLScreen = useMediaQuery(theme.breakpoints.up('xl'));

  // Determine if header should be transparent based on the page
  useEffect(() => {
    setIsTransparent(location.pathname === '/about' || location.pathname === '/');
  }, [location]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAccountAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountAnchorEl(null);
  };

  const handleLoginClick = () => {
    handleAccountMenuClose();
    setLoginDialogOpen(true);
  };

  const handleLoginDialogClose = () => {
    setLoginDialogOpen(false);
  };

  const handleLogout = async () => {
    handleAccountMenuClose();
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isCurrentUserAdmin = (): boolean => {
    return !!currentUser?.email && ADMIN_EMAILS.includes(currentUser.email);
  };

  return (
    <>
      <ElevationScroll>
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: 1200,
            backgroundColor: isTransparent ? 'transparent' : 'primary.dark',
            color: 'white', // Always white text
            boxShadow: isTransparent ? 'none' : '0 2px 10px rgba(0,0,0,0.1)',
          }}
        >
          <Box sx={{ 
            width: '100%',
            maxWidth: isXLScreen ? '1400px' : isLargeScreen ? '1200px' : '100%',
            mx: 'auto', // Center the content
            px: { xs: 2, sm: 3, md: 4 } // Reduced padding
          }}>
            <Toolbar sx={{ 
              py: { xs: 1, md: 1.5 },
              display: 'flex',
              justifyContent: 'space-between',
              position: 'relative',
              px: 0,
              minHeight: { xs: 56, md: 64 }, // Ensure consistent height
            }}>
              {/* Logo(s) - Left justified */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexBasis: { xs: '33%', md: '30%' },
                  justifyContent: 'flex-start',
                  pr: { xs: 1, md: 3 }, // Add right padding for spacing
                  gap: 2, // Space between logos
                }}
              >
                {tenantConfig?.tenant_id === 'evanston' ? (
                  // Evanston: Show all three logos side by side
                  <>
                    <Box 
                      component="a" 
                      href="https://www.propvizer.co/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        display: { xs: 'none', sm: 'block' },
                        transition: 'opacity 0.2s ease-in-out',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      <Box 
                        component="img" 
                        src={propvizerLogo} 
                        alt="Propvizer Logo" 
                        sx={{ height: { sm: 60, md: 70, lg: 80 } }} 
                      />
                    </Box>
                    <Box 
                      component="a" 
                      href="https://mpactcollective.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        display: { xs: 'none', sm: 'block' },
                        transition: 'opacity 0.2s ease-in-out',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      <Box 
                        component="img" 
                        src={mpactLogo} 
                        alt="MPact Logo" 
                        sx={{ height: { sm: 24, md: 28, lg: 32 } }} 
                      />
                    </Box>
                    <Box 
                      component="a" 
                      href="https://sentientco.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        display: { xs: 'none', sm: 'block' },
                        transition: 'opacity 0.2s ease-in-out',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      <Box 
                        component="img" 
                        src={sentientLogo} 
                        alt="Sentient Co Logo" 
                        sx={{ height: { sm: 54, md: 63, lg: 72 } }} 
                      />
                    </Box>
                  </>
                ) : (
                  // Other tenants: Show MPact logo linking to home
                  <Box 
                    component={RouterLink} 
                    to="/"
                    sx={{ 
                      display: { xs: 'none', sm: 'block' },
                      textDecoration: 'none',
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': { opacity: 0.8 }
                    }}
                  >
                    <Box 
                      component="img" 
                      src={mpactLogo} 
                      alt="MPact Logo" 
                      sx={{ height: 36 }} 
                    />
                  </Box>
                )}
              </Box>

              {/* Title - Center justified on all screen sizes */}
              <Box 
                sx={{ 
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: { xs: 'block', md: 'block' },
                  pointerEvents: 'none', // Ensures clicks go through to underlying elements
                  width: 'auto',
                  maxWidth: { xs: '200px', sm: '300px', md: '400px' },
                  px: { xs: 2, md: 4 }, // Add horizontal padding to prevent overlap
                }}
              >
                {/* Desktop Title */}
                <Typography
                  variant="h6"
                  sx={{ 
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', md: 'block' }
                  }}
                >
                  {tenantLoading ? 'Loading...' : (tenantConfig?.tenant_name || 'Community Project')}
                </Typography>
                
                {/* Mobile Title - centered */}
                <Typography
                  variant="h6"
                  sx={{ 
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    display: { xs: 'block', md: 'none' }
                  }}
                >
                  {tenantLoading ? 'Loading...' : (
                    tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 
                    tenantConfig?.tenant_id === 'severance' ? 'Severance Project' :
                    'Community Project'
                  )}
                </Typography>
              </Box>

              {/* Desktop Navigation - Right justified */}
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexBasis: '30%',
                gap: 1,
                pl: { xs: 1, md: 3 }, // Add left padding for spacing
              }}>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/about"
                  sx={{ 
                    py: 1,
                    fontWeight: 500,
                    borderBottom: location.pathname === '/about' ? '2px solid' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderBottom: '2px solid',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  About
                </Button>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/home"
                  sx={{ 
                    py: 1,
                    fontWeight: 500,
                    borderBottom: location.pathname === '/home' ? '2px solid' : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderBottom: '2px solid',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Ideas
                </Button>
                <Button 
                  variant="outlined"
                  component={RouterLink} 
                  to="/submit"
                  sx={{ 
                    ml: 1,
                    px: 2,
                    fontWeight: 500,
                    borderColor: 'white',
                    color: 'white',
                    textAlign: 'center',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Submit an Idea
                </Button>
                
                {isCurrentUserAdmin() && (
                  <Button
                    component={RouterLink}
                    to="/admin"
                    color="inherit"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      mx: 1,
                    }}
                  >
                    Admin
                  </Button>
                )}
                
                {/* Account Button */}
                <IconButton
                  color="inherit"
                  onClick={handleAccountMenu}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  {isAuthenticated && currentUser ? (
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: 'secondary.main', 
                        fontSize: '0.9rem' 
                      }}
                    >
                      {currentUser.email ? currentUser.email[0].toUpperCase() : 'U'}
                    </Avatar>
                  ) : (
                    <AccountCircleIcon />
                  )}
                </IconButton>
                <Menu
                  anchorEl={accountAnchorEl}
                  open={accountMenuOpen}
                  onClose={handleAccountMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  {isAuthenticated && currentUser ? (
                    <>
                      <MenuItem key="email" disabled>
                        <Typography variant="body2">{currentUser.email}</Typography>
                      </MenuItem>
                      <Divider key="divider" />
                      <MenuItem key="logout" onClick={handleLogout}>
                        <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                        Logout
                      </MenuItem>
                    </>
                  ) : (
                    <MenuItem onClick={handleLoginClick}>
                      <LoginIcon fontSize="small" sx={{ mr: 1 }} />
                      Login / Sign Up
                    </MenuItem>
                  )}
                </Menu>
              </Box>

              {/* Mobile Navigation */}
              <Box sx={{ 
                display: { xs: 'flex', md: 'none' },
                flexBasis: '33%',
                justifyContent: 'flex-end',
                alignItems: 'center',
                pl: { xs: 1, sm: 2 }, // Add left padding for spacing
              }}>
                {/* Account Button on Mobile */}
                <IconButton
                  color="inherit"
                  onClick={handleAccountMenu}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {isAuthenticated && currentUser ? (
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: 'secondary.main', 
                        fontSize: '0.9rem' 
                      }}
                    >
                      {currentUser.email ? currentUser.email[0].toUpperCase() : 'U'}
                    </Avatar>
                  ) : (
                    <AccountCircleIcon />
                  )}
                </IconButton>
              
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={open}
                  onClose={handleClose}
                >
                  <MenuItem 
                    component={RouterLink} 
                    to="/about" 
                    onClick={handleClose}
                  >
                    About
                  </MenuItem>
                  <MenuItem 
                    component={RouterLink} 
                    to="/home" 
                    onClick={handleClose}
                  >
                    Ideas
                  </MenuItem>
                  <MenuItem 
                    component={RouterLink} 
                    to="/submit" 
                    onClick={handleClose}
                    sx={{ color: 'secondary.main', fontWeight: 500 }}
                  >
                    Submit an Idea
                  </MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </Box>
        </AppBar>
      </ElevationScroll>
      
      {/* Login Dialog */}
      <Dialog 
        open={loginDialogOpen} 
        onClose={handleLoginDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <LoginForm onClose={handleLoginDialogClose} />
        </DialogContent>
      </Dialog>
      
      {/* Only add the spacer on non-About pages */}
      {!isTransparent && <Toolbar />}
    </>
  );
};

export default Header; 